import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import type { IHttpClient, ApiResponse, RequestConfig } from '@/shared/types/api';

export { ApiError } from './api.types';
export type { ApiResponse } from '@/shared/types/api';

function toAxiosConfig(config: RequestConfig = {}): AxiosRequestConfig {
  const axiosConfig: AxiosRequestConfig = {
    withCredentials: true,
  };

  if (config.headers) {
    axiosConfig.headers = config.headers;
  }
  if (config.timeout !== undefined) {
    axiosConfig.timeout = config.timeout;
  }
  if (config.params) {
    axiosConfig.params = config.params;
  }

  return axiosConfig;
}

function mapApiResponse<T>(response: { data: unknown; status: number }): ApiResponse<T> {
  return {
    data: response.data as T,
    status: response.status,
  };
}

export class ApiClient implements IHttpClient {
  private readonly client: AxiosInstance;
  private readonly baseUrl: string;

  constructor(axiosInstance: AxiosInstance, baseUrl: string) {
    this.client = axiosInstance;
    this.baseUrl = baseUrl;
  }

  async get<T = unknown>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get(url, toAxiosConfig(config));
    return mapApiResponse<T>(response);
  }

  async post<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post(url, data, toAxiosConfig(config));
    return mapApiResponse<T>(response);
  }

  async put<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put(url, data, toAxiosConfig(config));
    return mapApiResponse<T>(response);
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch(url, data, toAxiosConfig(config));
    return mapApiResponse<T>(response);
  }

  async delete<T = unknown>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete(url, toAxiosConfig(config));
    return mapApiResponse<T>(response);
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  isCancelError(error: unknown): boolean {
    return axios.isCancel(error);
  }
}

import { ApiClientFactory } from './ApiClientFactory';

const apiClient = ApiClientFactory.create();
export default apiClient;
